import { useState, useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import HeaderBackground from '../components/headerbackground.js';
import getAPI from '../functions/API/getAPI.js';
import postAPI from '../functions/API/postAPI.js';
import { toast } from '../lib/toast.js';
import GlobalContext from '../context/GlobalContext.js';
import { useOutletContext } from 'react-router-dom';
import moment from 'moment';
import {
    ClipboardCheck, ChevronRight, ChevronDown, CheckCircle2, Plus,
    MessageSquare, AlertTriangle, ArrowLeft, Check, Clock, Lock,
    Calendar, Upload, FileText, Send, Star, MinusCircle, X,
    ExternalLink, PenTool, CreditCard, Circle
} from 'lucide-react';

const ACTIVE_STATUSES = ['NOT_STARTED', 'IN_PROGRESS', 'AWAITING_ADMIN', 'OVERDUE'];

function getStatusInfo(item) {
    switch (item.status) {
        case 'NOT_STARTED':
            return { subtitle: 'Ready to start', badgeLabel: 'Action required', badgeBg: '#FEF3C7', badgeColor: '#D97706', iconBg: '#FEF3C7', iconColor: '#D97706' };
        case 'IN_PROGRESS':
            return { subtitle: item.due_date ? `Due ${moment(item.due_date).format('D MMM')}` : 'Awaiting your input', badgeLabel: 'Action required', badgeBg: '#FEF3C7', badgeColor: '#D97706', iconBg: '#FEF3C7', iconColor: '#D97706' };
        case 'AWAITING_ADMIN':
            return { subtitle: 'We are reviewing your request', badgeLabel: 'We are reviewing', badgeBg: '#F3F4F6', badgeColor: '#6B7280', iconBg: '#F3F4F620', iconColor: '#6B7280' };
        case 'OVERDUE':
            return { subtitle: item.due_date ? `Overdue · Due ${moment(item.due_date).format('D MMM')}` : 'Overdue', badgeLabel: 'Overdue', badgeBg: '#FEE2E2', badgeColor: '#DC2626', iconBg: '#FEE2E2', iconColor: '#DC2626' };
        case 'COMPLETED':
            if (item.completion_outcome === 'negative') {
                return { subtitle: `Closed ${moment(item.completed_at || item.updated_at).fromNow()}`, badgeLabel: null, iconBg: '#FEF3C7', iconColor: '#D97706', isCompleted: true, isNegative: true };
            }
            return { subtitle: `Completed ${moment(item.completed_at || item.updated_at).fromNow()}`, badgeLabel: null, iconBg: '#DCFCE7', iconColor: '#16A34A', isCompleted: true };
        case 'CANCELLED':
            return { subtitle: 'Cancelled', badgeLabel: null, iconBg: '#F3F4F6', iconColor: '#9CA3AF' };
        default:
            return { subtitle: '', badgeLabel: null, iconBg: '#F3F4F6', iconColor: '#9CA3AF' };
    }
}

function getExistingValue(field) {
    switch (field.field_type_snapshot) {
        case 'BOOLEAN': return field.value_boolean;
        case 'DATE': return field.value_date || null;
        case 'NUMBER': return field.value_number != null ? String(field.value_number) : '';
        case 'CURRENCY': return field.value_number != null ? String(parseFloat(Number(field.value_number).toFixed(2))) : '';
        case 'DROPDOWN':
            if (field.value_json) {
                try { return typeof field.value_json === 'string' ? JSON.parse(field.value_json) : field.value_json; } catch { return null; }
            }
            return null;
        case 'MULTI_SELECT':
            if (field.value_json) {
                try {
                    const v = typeof field.value_json === 'string' ? JSON.parse(field.value_json) : field.value_json;
                    return Array.isArray(v) ? v : [];
                } catch { return []; }
            }
            return [];
        default: return field.value_text || '';
    }
}

function getFieldOptions(field) {
    const rules = field.validation_rules_snapshot;
    if (!rules) return [];
    const parsed = typeof rules === 'string' ? JSON.parse(rules) : rules;
    return parsed?.options || [];
}

function getValidationRules(field) {
    const rules = field.validation_rules_snapshot;
    if (!rules) return {};
    return typeof rules === 'string' ? JSON.parse(rules) : rules;
}

function validateFieldRules(field, value) {
    const rules = getValidationRules(field);
    const type = field.field_type_snapshot;
    const errors = [];
    if (value === null || value === undefined || value === '') return errors;
    if (type === 'TEXT' || type === 'TEXTAREA') {
        const str = String(value);
        if (rules.min_length && str.length < rules.min_length) errors.push(`Must be at least ${rules.min_length} characters`);
        if (rules.max_length && str.length > rules.max_length) errors.push(`Must be no more than ${rules.max_length} characters`);
        if (rules.pattern) {
            try { if (!new RegExp(rules.pattern).test(str)) errors.push(rules.pattern_message || 'Invalid format'); } catch {}
        }
    }
    if (type === 'NUMBER' || type === 'CURRENCY') {
        const num = parseFloat(value);
        if (isNaN(num)) { errors.push('Must be a valid number'); }
        else {
            if (rules.min_value != null && num < rules.min_value) errors.push(`Must be at least ${rules.min_value}`);
            if (rules.max_value != null && num > rules.max_value) errors.push(`Must be no more than ${rules.max_value}`);
            if (type === 'NUMBER' && rules.allow_decimals === false && !Number.isInteger(num)) errors.push('Decimals are not allowed');
        }
    }
    if (type === 'DATE' && value) {
        const d = moment(value);
        const today = moment().startOf('day');
        if (rules.allow_past === false && d.isBefore(today)) errors.push('Past dates are not allowed');
        if (rules.allow_future === false && d.isAfter(today)) errors.push('Future dates are not allowed');
        if (rules.min_date && d.isBefore(moment(rules.min_date))) errors.push(`Date must be on or after ${moment(rules.min_date).format('D MMM YYYY')}`);
        if (rules.max_date && d.isAfter(moment(rules.max_date))) errors.push(`Date must be on or before ${moment(rules.max_date).format('D MMM YYYY')}`);
    }
    if (type === 'MULTI_SELECT' && Array.isArray(value)) {
        if (rules.min_selections && value.length < rules.min_selections) errors.push(`Select at least ${rules.min_selections}`);
        if (rules.max_selections && value.length > rules.max_selections) errors.push(`Select no more than ${rules.max_selections}`);
    }
    return errors;
}

export default function Requests() {
    const { theme, themeCSS, settings } = useContext(GlobalContext);
    const primaryColor = settings?.primary_hex_colour || '#813468';
    const location = useLocation();

    // ── List state ─────────────────────────────────────────────────────
    const [instances, setInstances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cancelledExpanded, setCancelledExpanded] = useState(false);
    const hasLoadedOnce = useRef(false);

    // ── New request sheet ──────────────────────────────────────────────
    const [showNewRequest, setShowNewRequest] = useState(false);
    const [flows, setFlows] = useState([]);
    const [flowsLoading, setFlowsLoading] = useState(false);
    const [selectedFlowId, setSelectedFlowId] = useState(null);
    const [creating, setCreating] = useState(false);

    // ── Detail state ───────────────────────────────────────────────────
    const [view, setView] = useState('list'); // 'list' | 'detail'
    const [selectedId, setSelectedId] = useState(null);
    const [instance, setInstance] = useState(null);
    const [instanceLoading, setInstanceLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [fieldValues, setFieldValues] = useState({});
    const [savingFields, setSavingFields] = useState({});
    const [fieldErrors, setFieldErrors] = useState({});
    const [activeTab, setActiveTab] = useState('request');
    const hasAutoStarted = useRef(false);

    // ── Messages ───────────────────────────────────────────────────────
    const [messages, setMessages] = useState([]);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [messageBody, setMessageBody] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const messagesFetched = useRef(false);
    const lastMessageIdRef = useRef(null);
    const messagesEndRef = useRef(null);

    // ── Feedback ───────────────────────────────────────────────────────
    const [feedbackRating, setFeedbackRating] = useState(0);
    const [feedbackComment, setFeedbackComment] = useState('');
    const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

    // ── Payment ────────────────────────────────────────────────────────
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [paymentModal, setPaymentModal] = useState(null);
    const [paymentResult, setPaymentResult] = useState(null); // 'success' | 'cancelled' | 'error'
    const [paymentStepId, setPaymentStepId] = useState(null);

    // ── Signing ────────────────────────────────────────────────────────
    const [signingLoading, setSigningLoading] = useState(false);
    const [signingStepId, setSigningStepId] = useState(null);

    // ── Upload ─────────────────────────────────────────────────────────
    const [uploadPickedFiles, setUploadPickedFiles] = useState({});   // { [stepId]: [{ file, name, size }] }
    const [uploadFileProgress, setUploadFileProgress] = useState({}); // { [stepId]: { [index]: { status, progress } } }
    const [uploadState, setUploadState] = useState({});               // { [stepId]: 'idle' | 'uploading' | 'completed' | 'error' }

    // ── Tab reset on nav re-tap ────────────────────────────────────────
    useEffect(() => {
        if (location.state?.openInstanceId) return; // handled by deep-link effect
        setView('list');
        setSelectedId(null);
        setInstance(null);
        fetchInstances(false);
    }, [location.key]);

    // ── Deep-link: open a specific instance (e.g. from home screen) ───
    useEffect(() => {
        const id = location.state?.openInstanceId;
        if (id) openDetail(id);
    }, []);

    // ── Fetch list ─────────────────────────────────────────────────────
    async function fetchInstances(showSkeleton = false) {
        if (showSkeleton || !hasLoadedOnce.current) setLoading(true);
        const res = await getAPI('taskinstances');
        if (res.response?.ok && res.data?.task_instances) {
            setInstances(res.data.task_instances);
        } else {
            setInstances([]);
        }
        setLoading(false);
        hasLoadedOnce.current = true;
    }

    useEffect(() => { fetchInstances(true); }, []);

    // ── Open new request sheet ─────────────────────────────────────────
    async function openNewRequest() {
        setSelectedFlowId(null);
        setShowNewRequest(true);
        setFlowsLoading(true);
        try {
            const res = await getAPI('taskflows');
            const list = res?.data?.task_flows;
            setFlows(Array.isArray(list) ? list : []);
        } catch { setFlows([]); }
        setFlowsLoading(false);
    }

    async function handleContinue() {
        if (creating || !selectedFlowId) return;
        setCreating(true);
        const res = await postAPI('taskinstances/request', { task_flow_id: selectedFlowId });
        if (res.response?.ok && res.data?.task_instance) {
            setShowNewRequest(false);
            setCreating(false);
            setSelectedFlowId(null);
            openDetail(res.data.task_instance.id);
        } else {
            toast.error(res.data?.message || 'Failed to create request');
            setCreating(false);
        }
    }

    // ── Open detail ────────────────────────────────────────────────────
    async function openDetail(id) {
        setSelectedId(id);
        setView('detail');
        setActiveTab('request');
        setInstance(null);
        setInstanceLoading(true);
        hasAutoStarted.current = false;
        messagesFetched.current = false;
        lastMessageIdRef.current = null;
        setMessages([]);
        setFeedbackRating(0);
        setFeedbackComment('');
        setFeedbackSubmitted(false);
        await fetchInstance(id);
        setInstanceLoading(false);
    }

    async function fetchInstance(id) {
        const res = await postAPI('taskinstances/get', { id: id || selectedId });
        if (res.response?.ok && res.data?.task_instance) {
            const inst = res.data.task_instance;
            setInstance(inst);
            const values = {};
            (inst.steps || []).forEach(step => {
                (step.fields || []).forEach(field => {
                    values[field.id] = getExistingValue(field);
                });
            });
            setFieldValues(values);
            if (inst.feedback) {
                setFeedbackRating(inst.feedback.rating);
                setFeedbackComment(inst.feedback.comment || '');
                setFeedbackSubmitted(true);
            }
            if (inst.status === 'NOT_STARTED' && !hasAutoStarted.current) {
                hasAutoStarted.current = true;
                postAPI('taskinstances/updatestatus', { id: inst.id, status: 'IN_PROGRESS' });
            }
            return inst;
        }
        return null;
    }

    async function silentRefresh() {
        const res = await postAPI('taskinstances/get', { id: selectedId });
        if (res.response?.ok && res.data?.task_instance) {
            const inst = res.data.task_instance;
            setInstance(inst);
            const values = {};
            (inst.steps || []).forEach(step => {
                (step.fields || []).forEach(field => {
                    values[field.id] = getExistingValue(field);
                });
            });
            setFieldValues(values);
        }
    }

    // ── Messages ───────────────────────────────────────────────────────
    async function fetchMessages(isInitial = false) {
        if (isInitial) setMessagesLoading(true);
        const res = await postAPI('taskmessages/get', { task_instance_id: selectedId });
        if (res.response?.ok && res.data?.messages) {
            const newMessages = res.data.messages;
            const newLastId = newMessages.length > 0 ? newMessages[newMessages.length - 1].id : null;
            if (isInitial || newLastId !== lastMessageIdRef.current) {
                setMessages(newMessages);
                lastMessageIdRef.current = newLastId;
            }
            setUnreadCount(0);
        }
        setMessagesLoading(false);
    }

    useEffect(() => {
        if (view !== 'detail' || !selectedId) return;
        if (activeTab === 'messages') {
            if (!messagesFetched.current) {
                messagesFetched.current = true;
                fetchMessages(true);
            }
            const interval = setInterval(() => fetchMessages(false), 5000);
            return () => clearInterval(interval);
        }
    }, [activeTab, view, selectedId]);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    async function handleSendMessage() {
        if (!messageBody.trim() || sendingMessage) return;
        setSendingMessage(true);
        const res = await postAPI('taskmessages/send', { task_instance_id: selectedId, body: messageBody.trim() });
        if (res.response?.ok) {
            setMessageBody('');
            await fetchMessages(false);
        } else {
            toast.error('Failed to send message');
        }
        setSendingMessage(false);
    }

    // ── Feedback ───────────────────────────────────────────────────────
    async function handleSubmitFeedback() {
        if (!feedbackRating || feedbackSubmitting) return;
        setFeedbackSubmitting(true);
        const res = await postAPI('taskinstances/feedback/submit', {
            task_instance_id: selectedId,
            rating: feedbackRating,
            comment: feedbackComment.trim() || null,
        });
        if (res.response?.ok) {
            setFeedbackSubmitted(true);
            toast.success('Thank you for your feedback');
        } else {
            toast.error(res.data?.message || 'Failed to submit feedback');
        }
        setFeedbackSubmitting(false);
    }

    // ── Field management ───────────────────────────────────────────────
    function updateLocalValue(fieldId, value) {
        setFieldValues(prev => ({ ...prev, [fieldId]: value }));
    }

    async function saveFieldValue(fieldId, value) {
        const val = value !== undefined ? value : fieldValues[fieldId];
        if (val === undefined) return;
        setSavingFields(prev => ({ ...prev, [fieldId]: true }));
        await postAPI('taskinstances/fields/updatevalue', { id: fieldId, value: val });
        setSavingFields(prev => ({ ...prev, [fieldId]: false }));
    }

    function handleTextBlur(fieldId) { saveFieldValue(fieldId); }

    async function handleSelectValue(fieldId, value) {
        updateLocalValue(fieldId, value);
        await saveFieldValue(fieldId, value);
        await silentRefresh();
    }

    async function handleMultiToggle(fieldId, optionValue) {
        const current = fieldValues[fieldId] || [];
        const updated = current.includes(optionValue) ? current.filter(v => v !== optionValue) : [...current, optionValue];
        updateLocalValue(fieldId, updated);
        await saveFieldValue(fieldId, updated);
        await silentRefresh();
    }

    // ── Step submission ────────────────────────────────────────────────
    async function handleSubmitStep(step) {
        const missingFields = (step.fields || []).filter(f => {
            if (!f.is_required_snapshot) return false;
            const val = fieldValues[f.id];
            if (val === null || val === undefined || val === '') return true;
            if (Array.isArray(val) && val.length === 0) return true;
            return false;
        });
        if (missingFields.length > 0) {
            toast.error(`Please complete: ${missingFields.map(f => f.name_snapshot).join(', ')}`);
            return;
        }
        const newErrors = {};
        (step.fields || []).forEach(f => {
            const errs = validateFieldRules(f, fieldValues[f.id]);
            if (errs.length > 0) newErrors[f.id] = errs;
        });
        setFieldErrors(prev => ({ ...prev, ...newErrors }));
        if (Object.values(newErrors).some(e => e.length > 0)) {
            toast.error('Please fix validation errors before continuing');
            return;
        }
        setSubmitting(true);
        await Promise.all((step.fields || []).filter(f => ['TEXT', 'TEXTAREA', 'NUMBER', 'CURRENCY'].includes(f.field_type_snapshot)).map(f => saveFieldValue(f.id)));
        const res = await postAPI('taskinstances/steps/updatestatus', { id: step.id, status: 'COMPLETED' });
        if (res.response?.ok) {
            await fetchInstance(selectedId);
        } else {
            toast.error('Failed to submit step');
        }
        setSubmitting(false);
    }

    async function handleAcknowledgeStep(step) {
        setSubmitting(true);
        const res = await postAPI('taskinstances/steps/updatestatus', { id: step.id, status: 'COMPLETED' });
        if (res.response?.ok) {
            await fetchInstance(selectedId);
        } else {
            toast.error('Failed to acknowledge step');
        }
        setSubmitting(false);
    }

    // ── Payment ────────────────────────────────────────────────────────
    async function handleInitiatePayment(step) {
        setPaymentLoading(true);
        setPaymentStepId(step.id);
        try {
            const res = await postAPI('taskinstances/steps/payment/initiate', { task_instance_step_id: step.id });
            if (res.response?.ok && res.data) {
                const { token, client_code, gateway_url, amount, customer_name } = res.data;
                const reason = step.name_snapshot || (instance?.task_flow_name || 'Task Payment');
                const customerRef = customer_name || 'Unknown';

                const params = {
                    token,
                    clientcode: client_code,
                    gatewayurl: gateway_url,
                    customerReference: customerRef,
                    amount: amount || 0,
                    paymentReason: reason,
                };

                setPaymentResult(null);
                setPaymentModal(params);

                // Submit the AllPay form directly into the iframe
                setTimeout(() => {
                    const form = document.createElement('form');
                    form.method = 'POST';
                    form.action = params.gatewayurl;
                    form.target = 'RequestsPaymentIFrame';
                    const fields = {
                        token: params.token,
                        customerReference: params.customerReference,
                        amount: params.amount,
                        clientCode: params.clientcode,
                        paymentReason: params.paymentReason,
                    };
                    Object.entries(fields).forEach(([k, v]) => {
                        const input = document.createElement('input');
                        input.type = 'hidden';
                        input.name = k;
                        input.value = v;
                        form.appendChild(input);
                    });
                    document.body.appendChild(form);
                    form.submit();
                    document.body.removeChild(form);
                }, 100);
            } else {
                toast.error(typeof res.data?.message === 'string' ? res.data.message : 'Failed to initiate payment');
            }
        } catch { toast.error('Failed to initiate payment'); }
        setPaymentLoading(false);
    }

    // Listen for AllPay payment completion messages
    useEffect(() => {
        function handlePaymentMessage(event) {
            if (!paymentModal) return;
            if (event.data.resultCode !== undefined) {
                if (event.data.resultCode === 0) {
                    setPaymentResult('success');
                } else if (event.data.resultCode < 0) {
                    setPaymentResult('cancelled');
                } else {
                    setPaymentResult('error');
                }
            }
        }
        window.addEventListener('message', handlePaymentMessage);
        return () => window.removeEventListener('message', handlePaymentMessage);
    }, [paymentModal]);

    async function closePaymentModal() {
        if (paymentResult === 'success') {
            try {
                await postAPI('taskinstances/steps/payment/complete', {
                    task_instance_step_id: paymentStepId,
                    transaction_id: 'allpay-' + paymentStepId + '-' + Date.now(),
                    status: 'success',
                });
                toast.success('Payment completed');
            } catch (e) {
                // Still close even if complete call fails
            }
            await fetchInstance(selectedId);
        }
        setPaymentModal(null);
        setPaymentResult(null);
        setPaymentStepId(null);
    }

    // ── Document signing ───────────────────────────────────────────────
    async function handleOpenSigning(step) {
        setSigningLoading(true);
        setSigningStepId(step.id);
        // Open the window synchronously so Safari doesn't block it as a popup
        const newWindow = window.open('', '_blank');
        try {
            const res = await postAPI('taskinstances/steps/signing/check', { task_instance_step_id: step.id });
            if (res.response?.ok && res.data?.signing_url) {
                newWindow.location.href = res.data.signing_url;
            } else {
                newWindow.close();
                toast.error('Unable to get signing link');
            }
        } catch {
            newWindow.close();
            toast.error('Failed to open document');
        }
        setSigningLoading(false);
        setSigningStepId(null);
    }

    async function handleCheckSigningStatus(step) {
        setSigningLoading(true);
        setSigningStepId(step.id);
        try {
            const res = await postAPI('taskinstances/steps/signing/check', { task_instance_step_id: step.id });
            if (res.response?.ok && res.data) {
                const status = res.data.status;
                if (status === 'signed') toast.success('Document signed successfully');
                else if (status === 'draft') toast.info('Document is being prepared');
                else if (status === 'sent' || status === 'waiting') toast.info('Awaiting signature');
                else if (status === 'cancelled' || status === 'declined') toast.error('Signing was ' + status);
                await fetchInstance(selectedId);
            } else {
                toast.error(res.data?.message || 'Failed to check signing status');
            }
        } catch { toast.error('Failed to check signing status'); }
        setSigningLoading(false);
        setSigningStepId(null);
    }

    async function handleIveSigned(step) {
        setSigningLoading(true);
        setSigningStepId(step.id);
        try {
            const res = await postAPI('taskinstances/steps/signing/check', { task_instance_step_id: step.id });
            if (res.response?.ok && res.data) {
                const { status, current_signer } = res.data;
                if (status === 'signed') {
                    toast.success('Document signed successfully');
                } else if (status === 'sent' || status === 'waiting') {
                    if (current_signer === 'admin') {
                        toast.success("You've signed — waiting for counter-signature");
                    } else {
                        toast.info('Awaiting signature — we\'ll check again when you return');
                    }
                } else if (['cancelled', 'declined', 'expired', 'recalled'].includes(status)) {
                    toast.error('Signing was ' + status);
                }
                await fetchInstance(selectedId);
            } else {
                toast.error('Failed to check signing status');
            }
        } catch { toast.error('Failed to check signing status'); }
        setSigningLoading(false);
        setSigningStepId(null);
    }

    // ── Document upload ────────────────────────────────────────────────
    function handleFileSelect(step, files) {
        if (!files?.length) return;
        const config = step.step_config_snapshot || {};
        const allowedTypes = config.allowed_types || ['pdf', 'jpg', 'png', 'doc', 'docx', 'xls', 'xlsx'];
        const maxSizeMb = config.max_size_mb || 50;
        const maxFiles = config.max_files || 5;

        const toAdd = [];
        const currentCount = (uploadPickedFiles[step.id] || []).length;

        for (const file of files) {
            if (currentCount + toAdd.length >= maxFiles) {
                toast.error(`You can upload up to ${maxFiles} files`);
                break;
            }
            const ext = file.name.split('.').pop().toLowerCase();
            if (allowedTypes.length > 0 && !allowedTypes.some(t => t.toLowerCase() === ext)) {
                toast.error(`File type .${ext} is not allowed`);
                continue;
            }
            if (file.size > maxSizeMb * 1024 * 1024) {
                toast.error(`${file.name} exceeds the ${maxSizeMb}MB limit`);
                continue;
            }
            toAdd.push({ file, name: file.name, size: file.size });
        }
        if (toAdd.length > 0) {
            setUploadPickedFiles(prev => ({ ...prev, [step.id]: [...(prev[step.id] || []), ...toAdd] }));
        }
    }

    function removePickedFile(stepId, index) {
        setUploadPickedFiles(prev => ({
            ...prev,
            [stepId]: (prev[stepId] || []).filter((_, i) => i !== index),
        }));
        setUploadFileProgress(prev => {
            const updated = { ...prev };
            if (updated[stepId]) {
                const stepProgress = { ...updated[stepId] };
                delete stepProgress[index];
                updated[stepId] = stepProgress;
            }
            return updated;
        });
    }

    function readFileAsBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    async function handleUploadAndComplete(step) {
        const picked = uploadPickedFiles[step.id] || [];
        if (picked.length === 0 || uploadState[step.id] === 'uploading') return;

        setUploadState(prev => ({ ...prev, [step.id]: 'uploading' }));

        try {
            for (let i = 0; i < picked.length; i++) {
                const { file, name, size } = picked[i];
                setUploadFileProgress(prev => ({ ...prev, [step.id]: { ...(prev[step.id] || {}), [i]: { status: 'uploading', progress: 0 } } }));

                const base64Data = await readFileAsBase64(file);

                const initRes = await postAPI('taskinstances/steps/upload/initiate', {
                    task_instance_step_id: step.id,
                    filename: name,
                    file_size: size,
                    file_index: i,
                });

                if (!initRes.response?.ok) {
                    const msg = initRes.data?.message;
                    toast.error(typeof msg === 'string' ? msg : 'Failed to start upload');
                    setUploadFileProgress(prev => ({ ...prev, [step.id]: { ...(prev[step.id] || {}), [i]: { status: 'error', progress: 0 } } }));
                    setUploadState(prev => ({ ...prev, [step.id]: 'error' }));
                    return;
                }

                const { file_id, chunk_size } = initRes.data;
                const chunkSizeBytes = chunk_size || 4 * 1024 * 1024;
                const base64ChunkSize = Math.ceil(chunkSizeBytes / 3) * 4;
                const totalChunks = Math.ceil(base64Data.length / base64ChunkSize);

                for (let c = 0; c < totalChunks; c++) {
                    const chunkStart = c * base64ChunkSize;
                    const chunkEnd = Math.min(chunkStart + base64ChunkSize, base64Data.length);
                    const chunkData = base64Data.substring(chunkStart, chunkEnd);

                    const chunkRes = await postAPI('taskinstances/steps/upload/chunk', {
                        task_instance_step_id: step.id,
                        file_id,
                        chunk_data: chunkData,
                        chunk_index: c,
                    });

                    if (!chunkRes.response?.ok) {
                        toast.error('Upload failed');
                        setUploadFileProgress(prev => ({ ...prev, [step.id]: { ...(prev[step.id] || {}), [i]: { status: 'error', progress: 0 } } }));
                        setUploadState(prev => ({ ...prev, [step.id]: 'error' }));
                        return;
                    }

                    const progress = Math.round(((c + 1) / totalChunks) * 100);
                    setUploadFileProgress(prev => ({ ...prev, [step.id]: { ...(prev[step.id] || {}), [i]: { status: 'uploading', progress } } }));
                }

                setUploadFileProgress(prev => ({ ...prev, [step.id]: { ...(prev[step.id] || {}), [i]: { status: 'scanning', progress: 100 } } }));
            }

            // Complete upload
            const completeRes = await postAPI('taskinstances/steps/upload/complete', {
                task_instance_step_id: step.id,
            });

            if (completeRes.response?.status === 422) {
                const rejectedFiles = completeRes.data?.rejected_files || [];
                const rejectedByName = {};
                for (const rf of rejectedFiles) {
                    rejectedByName[rf.filename] = true;
                }
                setUploadFileProgress(prev => {
                    const updated = { ...(prev[step.id] || {}) };
                    for (let i = 0; i < picked.length; i++) {
                        if (rejectedByName[picked[i].name]) {
                            updated[i] = { status: 'rejected', progress: 100 };
                        } else if (updated[i]?.status === 'scanning') {
                            updated[i] = { status: 'done', progress: 100 };
                        }
                    }
                    return { ...prev, [step.id]: updated };
                });
                toast.error('Some files were rejected');
                setUploadState(prev => ({ ...prev, [step.id]: 'error' }));
                return;
            }

            if (!completeRes.response?.ok) {
                toast.error(typeof completeRes.data?.message === 'string' ? completeRes.data.message : 'Failed to complete upload');
                setUploadState(prev => ({ ...prev, [step.id]: 'error' }));
                return;
            }

            // Success
            setUploadFileProgress(prev => {
                const updated = { ...(prev[step.id] || {}) };
                for (const key of Object.keys(updated)) {
                    if (updated[key]?.status === 'scanning') {
                        updated[key] = { status: 'done', progress: 100 };
                    }
                }
                return { ...prev, [step.id]: updated };
            });
            setUploadState(prev => ({ ...prev, [step.id]: 'completed' }));
            toast.success('Files uploaded successfully');
            await fetchInstance(selectedId);
        } catch (e) {
            toast.error('Upload failed');
            setUploadState(prev => ({ ...prev, [step.id]: 'error' }));
        }
    }

    // ── Step layout ────────────────────────────────────────────────────
    function getStepLayout() {
        if (!instance?.steps) return { completed: [], active: null, waiting: [], upcoming: [] };
        const allSorted = [...instance.steps].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
        const firstIncompletePos = allSorted.find(s =>
            (s.is_conditionally_visible !== false && s.is_conditionally_visible !== 0) && s.status !== 'COMPLETED'
        )?.position ?? Infinity;
        const completed = [], waiting = [], upcoming = [];
        let active = null;
        for (const step of allSorted) {
            const isHidden = step.is_conditionally_visible === false || step.is_conditionally_visible === 0;
            if (isHidden) {
                if ((step.position ?? 0) < firstIncompletePos) completed.push({ ...step, _skipped: true });
                continue;
            }
            if (step.status === 'COMPLETED') {
                completed.push(step);
            } else if (step.status === 'PENDING_APPROVAL') {
                completed.push({ ...step, _pendingApproval: true });
            } else if (!active && step.actor_type_snapshot === 'tenant' && step.status !== 'BLOCKED') {
                active = step;
            } else if (step.actor_type_snapshot === 'admin' && step.status !== 'BLOCKED') {
                waiting.push(step);
            } else {
                upcoming.push(step);
            }
        }
        return { completed, active, waiting, upcoming };
    }

    // ── Field renderer ─────────────────────────────────────────────────
    function renderField(field) {
        const type = field.field_type_snapshot;
        const required = field.is_required_snapshot;
        const label = field.name_snapshot + (required ? ' *' : '');
        const placeholder = field.placeholder_snapshot || '';
        const helpText = field.help_text_snapshot;
        const helpUrl = field.help_url_snapshot;
        const rules = getValidationRules(field);
        const errors = fieldErrors[field.id] || [];

        return (
            <div key={field.id} className="mb-4">
                <label className={`block text-sm font-medium mb-1.5 ${themeCSS.cardTitle}`}>{label}</label>

                {type === 'TEXT' && (
                    <input
                        type="text"
                        value={fieldValues[field.id] || ''}
                        onChange={e => updateLocalValue(field.id, e.target.value)}
                        onBlur={() => handleTextBlur(field.id)}
                        placeholder={placeholder || 'Enter text'}
                        maxLength={rules.max_length || undefined}
                        className={`w-full rounded-xl border px-3 py-2.5 text-sm ${themeCSS.cardBg} ${themeCSS.cardText} ${themeCSS.cardBorder} focus:outline-none focus:ring-2`}
                        style={{ '--tw-ring-color': primaryColor + '40' }}
                    />
                )}
                {type === 'TEXTAREA' && (
                    <textarea
                        value={fieldValues[field.id] || ''}
                        onChange={e => updateLocalValue(field.id, e.target.value)}
                        onBlur={() => handleTextBlur(field.id)}
                        placeholder={placeholder || 'Enter details'}
                        maxLength={rules.max_length || undefined}
                        rows={4}
                        className={`w-full rounded-xl border px-3 py-2.5 text-sm ${themeCSS.cardBg} ${themeCSS.cardText} ${themeCSS.cardBorder} focus:outline-none resize-none`}
                    />
                )}
                {type === 'NUMBER' && (
                    <input
                        type="number"
                        value={fieldValues[field.id] != null ? String(fieldValues[field.id]) : ''}
                        onChange={e => updateLocalValue(field.id, e.target.value)}
                        onBlur={() => handleTextBlur(field.id)}
                        placeholder={placeholder || '0'}
                        step={rules.allow_decimals === false ? '1' : 'any'}
                        className={`w-full rounded-xl border px-3 py-2.5 text-sm ${themeCSS.cardBg} ${themeCSS.cardText} ${themeCSS.cardBorder} focus:outline-none`}
                    />
                )}
                {type === 'CURRENCY' && (
                    <div className="relative">
                        <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium ${themeCSS.cardSubtext}`}>£</span>
                        <input
                            type="number"
                            value={fieldValues[field.id] != null ? String(parseFloat(Number(fieldValues[field.id]).toFixed(2)) || '') : ''}
                            onChange={e => updateLocalValue(field.id, e.target.value)}
                            onBlur={() => handleTextBlur(field.id)}
                            placeholder={placeholder || '0.00'}
                            step="0.01"
                            className={`w-full rounded-xl border pl-7 pr-3 py-2.5 text-sm ${themeCSS.cardBg} ${themeCSS.cardText} ${themeCSS.cardBorder} focus:outline-none`}
                        />
                    </div>
                )}
                {type === 'DATE' && (
                    <input
                        type="date"
                        value={fieldValues[field.id] || ''}
                        onChange={e => updateLocalValue(field.id, e.target.value)}
                        onInput={e => updateLocalValue(field.id, e.target.value)}
                        onBlur={e => {
                            const val = e.target.value;
                            updateLocalValue(field.id, val);
                            saveFieldValue(field.id, val);
                        }}
                        min={rules.allow_past === false ? moment().format('YYYY-MM-DD') : rules.min_date || undefined}
                        max={rules.allow_future === false ? moment().format('YYYY-MM-DD') : rules.max_date || undefined}
                        className={`w-full rounded-xl border px-3 py-2.5 text-sm ${themeCSS.cardBg} ${themeCSS.cardText} ${themeCSS.cardBorder} focus:outline-none`}
                    />
                )}
                {type === 'BOOLEAN' && (
                    <div className="flex gap-2">
                        {[{ label: 'Yes', val: true }, { label: 'No', val: false }].map(opt => {
                            const selected = fieldValues[field.id] === opt.val;
                            return (
                                <button
                                    key={opt.label}
                                    onClick={() => handleSelectValue(field.id, opt.val)}
                                    className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-colors ${selected ? 'text-white border-transparent' : `${themeCSS.cardText} ${themeCSS.cardBorder} ${themeCSS.cardHover}`}`}
                                    style={selected ? { backgroundColor: primaryColor } : undefined}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                )}
                {type === 'DROPDOWN' && (() => {
                    const options = getFieldOptions(field);
                    return (
                        <div className="space-y-2">
                            {options.map(opt => {
                                const selected = fieldValues[field.id] === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        onClick={() => handleSelectValue(field.id, opt.value)}
                                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm border transition-colors ${selected ? 'text-white border-transparent' : `${themeCSS.cardText} ${themeCSS.cardBorder} ${themeCSS.cardHover}`}`}
                                        style={selected ? { backgroundColor: primaryColor } : undefined}
                                    >
                                        {opt.label}
                                        {selected && <Check size={14} className="text-white" />}
                                    </button>
                                );
                            })}
                        </div>
                    );
                })()}
                {type === 'MULTI_SELECT' && (() => {
                    const options = getFieldOptions(field);
                    const selected = fieldValues[field.id] || [];
                    const maxSel = rules.max_selections;
                    const atMax = maxSel && selected.length >= maxSel;
                    return (
                        <div className="space-y-2">
                            {maxSel && <p className={`text-xs ${themeCSS.cardMuted} mb-1`}>Select up to {maxSel}</p>}
                            {options.map(opt => {
                                const isSelected = selected.includes(opt.value);
                                const disabled = atMax && !isSelected;
                                return (
                                    <button
                                        key={opt.value}
                                        onClick={() => !disabled && handleMultiToggle(field.id, opt.value)}
                                        disabled={disabled}
                                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm border transition-colors ${isSelected ? 'text-white border-transparent' : `${themeCSS.cardText} ${themeCSS.cardBorder} ${themeCSS.cardHover}`} disabled:opacity-40`}
                                        style={isSelected ? { backgroundColor: primaryColor } : undefined}
                                    >
                                        {opt.label}
                                        {isSelected && <Check size={14} className="text-white" />}
                                    </button>
                                );
                            })}
                        </div>
                    );
                })()}

                {(helpText || helpUrl) && (
                    <div className="flex items-center gap-2 mt-1.5">
                        {helpText && <p className={`text-xs ${themeCSS.cardMuted}`}>{helpText}</p>}
                        {helpUrl && (
                            <a href={helpUrl} target="_blank" rel="noreferrer" className={themeCSS.cardMuted}>
                                <ExternalLink size={12} />
                            </a>
                        )}
                    </div>
                )}
                {errors.map((err, i) => <p key={i} className="text-xs text-red-500 mt-1">{err}</p>)}
            </div>
        );
    }

    // ── Skeleton ───────────────────────────────────────────────────────
    function SkeletonList() {
        return (
            <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                    <div key={i} className={`${themeCSS.cardBg} rounded-xl p-4 flex items-center gap-3`}>
                        <div className={`w-10 h-10 rounded-full ${themeCSS.skeleton} animate-pulse flex-shrink-0`} />
                        <div className="flex-1 space-y-2">
                            <div className={`h-4 rounded-md ${themeCSS.skeleton} animate-pulse w-3/4`} />
                            <div className={`h-3 rounded ${themeCSS.skeleton} animate-pulse w-1/2`} />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    // ── List view ──────────────────────────────────────────────────────
    function ListView() {
        const activeInstances = instances.filter(i => ACTIVE_STATUSES.includes(i.status));
        const completedInstances = instances.filter(i => i.status === 'COMPLETED');
        const cancelledInstances = instances.filter(i => i.status === 'CANCELLED');

        function renderItem(item) {
            const info = getStatusInfo(item);
            const isRecentlyCompleted = item.status === 'COMPLETED' && item.completed_at && moment().diff(moment(item.completed_at), 'days') < 7;
            return (
                <button
                    key={item.id}
                    onClick={() => openDetail(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3.5 text-left ${themeCSS.cardHover} transition-colors border-b last:border-b-0 ${themeCSS.cardBorder}`}
                >
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: info.iconBg }}>
                        {info.isCompleted
                            ? (info.isNegative ? <AlertTriangle size={18} style={{ color: info.iconColor }} /> : <CheckCircle2 size={18} style={{ color: info.iconColor }} />)
                            : <ClipboardCheck size={18} style={{ color: info.iconColor }} />}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <p className={`text-sm font-medium leading-snug ${themeCSS.cardTitle}`}>{item.task_flow_name}</p>
                            {item.unread_message_count > 0 && (
                                <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: primaryColor }}>
                                    <MessageSquare size={9} /> {item.unread_message_count}
                                </span>
                            )}
                            {info.badgeLabel && (
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: info.badgeBg, color: info.badgeColor }}>
                                    {info.badgeLabel}
                                </span>
                            )}
                            {isRecentlyCompleted && (
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1" style={{ backgroundColor: '#DCFCE7', color: '#16A34A' }}>
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />Recent
                                </span>
                            )}
                        </div>
                        <p className={`text-xs mt-0.5 ${themeCSS.cardMuted}`}>{info.subtitle}</p>
                    </div>
                    <ChevronRight size={16} className={themeCSS.cardMuted} />
                </button>
            );
        }

        if (loading) return <SkeletonList />;

        const desktopNewButton = (
            <div className="hidden lg:flex justify-end mb-4">
                <button
                    onClick={openNewRequest}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white"
                    style={{ backgroundColor: primaryColor }}
                >
                    <Plus size={16} /> New Request
                </button>
            </div>
        );

        if (instances.length === 0) {
            return (
                <>
                    {desktopNewButton}
                    <div className={`${themeCSS.cardBg} rounded-2xl p-8 flex flex-col items-center text-center`} style={{ border: `2px dashed ${theme === 'dark' ? '#334155' : '#F2F2F7'}` }}>
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-50'}`}>
                            <ClipboardCheck size={28} className="text-gray-300" strokeWidth={1.5} />
                        </div>
                        <p className={`text-base font-semibold mb-2 ${themeCSS.cardSubtext}`}>No requests yet</p>
                        <p className={`text-sm ${themeCSS.cardMuted}`}>Tap the + button to start a new request</p>
                    </div>
                </>
            );
        }

        return (
            <div className="space-y-4">
                {desktopNewButton}
                {activeInstances.length > 0 && (
                    <div>
                        <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${themeCSS.cardMuted}`}>Open ({activeInstances.length})</p>
                        <div className={`${themeCSS.cardBg} rounded-2xl overflow-hidden shadow-sm`}>
                            {activeInstances.map(renderItem)}
                        </div>
                    </div>
                )}
                {completedInstances.length > 0 && (
                    <div>
                        <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${themeCSS.cardMuted}`}>Closed ({completedInstances.length})</p>
                        <div className={`${themeCSS.cardBg} rounded-2xl overflow-hidden shadow-sm`}>
                            {completedInstances.map(renderItem)}
                        </div>
                    </div>
                )}
                {cancelledInstances.length > 0 && (
                    <div>
                        <button
                            onClick={() => setCancelledExpanded(!cancelledExpanded)}
                            className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wider mb-2 ${themeCSS.cardMuted}`}
                        >
                            Cancelled ({cancelledInstances.length})
                            <ChevronDown size={14} className={`transition-transform ${cancelledExpanded ? 'rotate-180' : ''}`} />
                        </button>
                        {cancelledExpanded && (
                            <div className={`${themeCSS.cardBg} rounded-2xl overflow-hidden shadow-sm`}>
                                {cancelledInstances.map(renderItem)}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // ── Detail view ────────────────────────────────────────────────────
    function DetailView() {
        if (instanceLoading || !instance) {
            return (
                <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className={`${themeCSS.cardBg} rounded-xl p-4`}>
                            <div className={`h-4 rounded-md ${themeCSS.skeleton} animate-pulse w-2/3 mb-2`} />
                            <div className={`h-3 rounded ${themeCSS.skeleton} animate-pulse w-full`} />
                        </div>
                    ))}
                </div>
            );
        }

        const { completed, active, waiting, upcoming } = getStepLayout();
        const visibleSteps = (instance.steps || []).filter(s => s.is_conditionally_visible !== false && s.is_conditionally_visible !== 0);
        const completedCount = visibleSteps.filter(s => s.status === 'COMPLETED').length;
        const totalSteps = visibleSteps.length;
        const isInstanceDone = ['COMPLETED', 'CANCELLED'].includes(instance.status);
        const hasPendingApproval = completed.some(s => s._pendingApproval);
        const awaitingOutcome = !isInstanceDone && !active && completedCount === totalSteps && totalSteps > 0;

        return (
            <div>
                {/* Tab switcher */}
                <div className={`mb-4 rounded-2xl shadow-sm p-1 flex ${themeCSS.cardBg}`}>
                    {['request', 'messages'].map(tab => {
                        const isActive = activeTab === tab;
                        return (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all ${isActive ? 'text-white' : themeCSS.cardMuted}`}
                                style={isActive ? { backgroundColor: primaryColor } : undefined}
                            >
                                {tab === 'request' ? 'Request' : 'Messages'}
                                {tab === 'messages' && unreadCount > 0 && (
                                    <span className="text-[10px] font-bold bg-white/30 px-1.5 py-0.5 rounded-full">{unreadCount}</span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {activeTab === 'request' ? RequestTab() : MessagesTab()}
            </div>
        );

        function RequestTab() {
            return (
                <div className="space-y-3">
                    {/* Completed steps */}
                    {completed.map(step => (
                        <div key={step.id} className={`${themeCSS.cardBg} rounded-2xl px-4 py-3 flex items-center gap-3 shadow-sm opacity-70`}>
                            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: step._skipped ? '#F3F4F6' : step._pendingApproval ? '#FEF3C7' : '#DCFCE7' }}>
                                {step._skipped
                                    ? <MinusCircle size={16} className="text-gray-400" />
                                    : step._pendingApproval
                                        ? <Clock size={16} style={{ color: '#D97706' }} />
                                        : <Check size={16} style={{ color: '#16A34A' }} />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium ${themeCSS.cardTitle}`}>{step.name_snapshot}</p>
                                <p className={`text-xs ${themeCSS.cardMuted}`}>{step._skipped ? 'Skipped' : step._pendingApproval ? 'Pending approval' : 'Completed'}</p>
                            </div>
                        </div>
                    ))}

                    {/* Active step */}
                    {active && renderActiveStep(active)}

                    {/* Waiting on admin steps */}
                    {waiting.map(step => (
                        <div key={step.id} className={`${themeCSS.cardBg} rounded-2xl px-4 py-3 flex items-center gap-3 shadow-sm`}>
                            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${primaryColor}14` }}>
                                <Clock size={16} style={{ color: primaryColor }} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium ${themeCSS.cardTitle}`}>{step.name_snapshot}</p>
                                <p className={`text-xs ${themeCSS.cardMuted}`}>Waiting for us</p>
                            </div>
                        </div>
                    ))}

                    {/* Upcoming / blocked steps */}
                    {upcoming.map(step => (
                        <div key={step.id} className={`${themeCSS.cardBg} rounded-2xl px-4 py-3 flex items-center gap-3 shadow-sm opacity-50`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-100'}`}>
                                <Lock size={16} className={themeCSS.cardMuted} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium ${themeCSS.cardTitle}`}>{step.name_snapshot}</p>
                                <p className={`text-xs ${themeCSS.cardMuted}`}>Upcoming</p>
                            </div>
                        </div>
                    ))}

                    {/* Awaiting outcome */}
                    {awaitingOutcome && (
                        <div className={`${themeCSS.cardBg} rounded-2xl p-5 text-center shadow-sm`}>
                            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: `${primaryColor}14` }}>
                                <Clock size={24} style={{ color: primaryColor }} />
                            </div>
                            <p className={`text-sm font-semibold ${themeCSS.cardTitle}`}>Waiting for review</p>
                            <p className={`text-xs mt-1 ${themeCSS.cardMuted}`}>All steps are complete — we're reviewing your request</p>
                        </div>
                    )}

                    {/* Done / cancelled state */}
                    {isInstanceDone && (
                        <div className={`${themeCSS.cardBg} rounded-2xl p-5 shadow-sm`}>
                            <div className="flex flex-col items-center text-center mb-4">
                                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: instance.status === 'CANCELLED' ? '#F3F4F6' : instance.completion_outcome === 'negative' ? '#FEF3C7' : '#DCFCE7' }}>
                                    {instance.status === 'CANCELLED'
                                        ? <X size={26} className="text-gray-400" />
                                        : instance.completion_outcome === 'negative'
                                            ? <AlertTriangle size={26} style={{ color: '#D97706' }} />
                                            : <CheckCircle2 size={26} style={{ color: '#16A34A' }} />}
                                </div>
                                <p className={`text-base font-semibold ${themeCSS.cardTitle}`}>
                                    {instance.status === 'CANCELLED' ? 'Request Cancelled' : instance.completion_outcome === 'negative' ? 'Request Closed' : 'Request Complete'}
                                </p>
                                {instance.completion_message && (
                                    <p className={`text-sm mt-1 ${themeCSS.cardMuted}`}>{instance.completion_message}</p>
                                )}
                            </div>

                            {/* Feedback */}
                            {instance.status === 'COMPLETED' && (
                                <div className={`border-t pt-4 ${themeCSS.cardBorder}`}>
                                    <p className={`text-sm font-semibold mb-3 text-center ${themeCSS.cardTitle}`}>
                                        {feedbackSubmitted ? 'Your Feedback' : 'How did we do?'}
                                    </p>
                                    <div className="flex justify-center gap-2 mb-3">
                                        {[1, 2, 3, 4, 5].map(star => (
                                            <button
                                                key={star}
                                                onClick={() => !feedbackSubmitted && setFeedbackRating(star)}
                                                disabled={feedbackSubmitted}
                                                className="transition-transform hover:scale-110 disabled:cursor-default"
                                            >
                                                <Star
                                                    size={28}
                                                    fill={star <= feedbackRating ? '#F59E0B' : 'none'}
                                                    className={star <= feedbackRating ? 'text-amber-400' : themeCSS.cardMuted}
                                                    strokeWidth={1.5}
                                                />
                                            </button>
                                        ))}
                                    </div>
                                    {!feedbackSubmitted && feedbackRating > 0 && (
                                        <div className="space-y-3">
                                            <textarea
                                                value={feedbackComment}
                                                onChange={e => setFeedbackComment(e.target.value)}
                                                placeholder="Additional comments (optional)"
                                                rows={3}
                                                className={`w-full rounded-xl border px-3 py-2.5 text-sm ${themeCSS.cardBg} ${themeCSS.cardText} ${themeCSS.cardBorder} focus:outline-none resize-none`}
                                            />
                                            <button
                                                onClick={handleSubmitFeedback}
                                                disabled={feedbackSubmitting}
                                                className="w-full py-3 rounded-2xl text-sm font-semibold text-white transition-opacity disabled:opacity-60"
                                                style={{ backgroundColor: primaryColor }}
                                            >
                                                {feedbackSubmitting ? 'Submitting...' : 'Submit Feedback'}
                                            </button>
                                        </div>
                                    )}
                                    {feedbackSubmitted && feedbackComment && (
                                        <p className={`text-sm text-center ${themeCSS.cardMuted}`}>"{feedbackComment}"</p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        }

        function renderActiveStep(step) {
            const stepType = step.step_type_snapshot || 'standard';
            return (
                <div key={step.id} className={`${themeCSS.cardBg} rounded-2xl shadow-sm overflow-hidden`}>
                    {/* Step header */}
                    <div className="px-4 pt-4 pb-3">
                        <p className={`text-base font-bold ${themeCSS.cardTitle}`}>{step.name_snapshot}</p>
                        {step.description_snapshot && (
                            <p className={`text-sm mt-1 ${themeCSS.cardMuted}`}>{step.description_snapshot}</p>
                        )}
                    </div>

                    <div className="px-4 pb-4">
                        {/* Fields */}
                        {stepType === 'standard' && (step.fields || []).map(field => renderField(field))}

                        {/* Info step */}
                        {stepType === 'info' && step.info_text_snapshot && (
                            <div className={`rounded-xl p-3 mb-3 text-sm ${theme === 'dark' ? 'bg-slate-700 text-slate-200' : 'bg-blue-50 text-blue-800'}`}>
                                {step.info_text_snapshot}
                            </div>
                        )}

                        {/* Payment step */}
                        {stepType === 'payment' && (
                            <div className="space-y-2">
                                {step.step_config_snapshot?.amount && (
                                    <div className={`flex items-center justify-between rounded-xl p-3 ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-50'}`}>
                                        <p className={`text-sm ${themeCSS.cardMuted}`}>Amount</p>
                                        <p className={`text-base font-bold ${themeCSS.cardTitle}`}>£{parseFloat(step.step_config_snapshot.amount).toFixed(2)}</p>
                                    </div>
                                )}
                                <button
                                    onClick={() => handleInitiatePayment(step)}
                                    disabled={paymentLoading}
                                    className="w-full py-3 rounded-2xl text-sm font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-60"
                                    style={{ backgroundColor: primaryColor }}
                                >
                                    <CreditCard size={16} />
                                    {paymentLoading ? 'Loading...' : 'Make Payment'}
                                </button>
                            </div>
                        )}

                        {/* Document upload step */}
                        {stepType === 'document_upload' && (() => {
                            const picked = uploadPickedFiles[step.id] || [];
                            const progress = uploadFileProgress[step.id] || {};
                            const stepUploadState = uploadState[step.id] || 'idle';
                            const isUploading = stepUploadState === 'uploading';
                            const config = step.step_config_snapshot || {};
                            const allowedTypes = config.allowed_types || ['pdf', 'jpg', 'png', 'doc', 'docx', 'xls', 'xlsx'];

                            return (
                                <div className="space-y-2">
                                    {/* File picker */}
                                    {!isUploading && (
                                        <label className={`w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-semibold border-2 border-dashed cursor-pointer ${themeCSS.cardBorder} ${themeCSS.cardHover} ${themeCSS.cardMuted}`}>
                                            <Upload size={16} />
                                            Choose Files
                                            <input
                                                type="file"
                                                className="hidden"
                                                multiple
                                                accept={allowedTypes.map(t => '.' + t).join(',')}
                                                onChange={e => { handleFileSelect(step, e.target.files); e.target.value = ''; }}
                                            />
                                        </label>
                                    )}
                                    {allowedTypes.length > 0 && picked.length === 0 && (
                                        <p className={`text-xs text-center ${themeCSS.cardMuted}`}>
                                            {allowedTypes.map(t => t.toUpperCase()).join(', ')} up to {config.max_size_mb || 50}MB
                                        </p>
                                    )}

                                    {/* Picked file list */}
                                    {picked.length > 0 && (
                                        <div className="space-y-1.5">
                                            {picked.map((f, idx) => {
                                                const fp = progress[idx];
                                                return (
                                                    <div key={idx} className={`flex items-center gap-2 rounded-xl px-3 py-2.5 ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-50'}`}>
                                                        <FileText size={14} className={themeCSS.cardMuted} />
                                                        <div className="flex-1 min-w-0">
                                                            <p className={`text-sm truncate ${themeCSS.cardTitle}`}>{f.name}</p>
                                                            <p className={`text-xs ${themeCSS.cardMuted}`}>
                                                                {f.size < 1024 * 1024 ? `${(f.size / 1024).toFixed(0)}KB` : `${(f.size / 1024 / 1024).toFixed(1)}MB`}
                                                                {fp?.status === 'uploading' && fp.progress > 0 && ` · ${fp.progress}%`}
                                                            </p>
                                                        </div>
                                                        {!fp && !isUploading && (
                                                            <button onClick={() => removePickedFile(step.id, idx)} className={themeCSS.cardMuted}>
                                                                <X size={14} />
                                                            </button>
                                                        )}
                                                        {fp?.status === 'uploading' && (
                                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400 flex-shrink-0" />
                                                        )}
                                                        {fp?.status === 'scanning' && (
                                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-gray-400" />
                                                                <span className={`text-xs ${themeCSS.cardMuted}`}>Scanning</span>
                                                            </div>
                                                        )}
                                                        {fp?.status === 'done' && (
                                                            <CheckCircle2 size={16} className="text-green-500 flex-shrink-0" />
                                                        )}
                                                        {(fp?.status === 'error' || fp?.status === 'rejected') && (
                                                            <AlertTriangle size={16} className="text-red-500 flex-shrink-0" />
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Upload button */}
                                    {picked.length > 0 && (
                                        <button
                                            onClick={() => handleUploadAndComplete(step)}
                                            disabled={isUploading || picked.length === 0}
                                            className="w-full py-3 rounded-2xl text-sm font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-60"
                                            style={{ backgroundColor: primaryColor }}
                                        >
                                            <Upload size={16} />
                                            {isUploading ? 'Uploading...' : `Upload & Submit (${picked.length})`}
                                        </button>
                                    )}
                                </div>
                            );
                        })()}

                        {/* Document signing step */}
                        {stepType === 'document_signing' && (() => {
                            const hasAgreement = !!step.step_result?.agreement_id && step.step_result?.status !== 'cancelled' && step.step_result?.status !== 'draft';
                            const companyName = settings?.company_name || 'us';
                            const isLoading = signingLoading && signingStepId === step.id;

                            return (
                                <div className="space-y-3">
                                    {/* Status display */}
                                    <div className="text-center py-2">
                                        {!hasAgreement ? (
                                            <>
                                                <div className={`mx-auto flex h-10 w-10 items-center justify-center rounded-full mb-2 bg-amber-50`}>
                                                    <PenTool size={18} className="text-amber-600" />
                                                </div>
                                                <p className={`text-sm font-medium ${themeCSS.cardTitle}`}>Waiting for document to be prepared</p>
                                                <p className={`text-xs mt-1 ${themeCSS.cardMuted}`}>You'll be able to sign it once it's ready.</p>
                                            </>
                                        ) : step.step_result?.current_signer === 'admin' ? (
                                            <>
                                                <div className={`mx-auto flex h-10 w-10 items-center justify-center rounded-full mb-2 bg-amber-50`}>
                                                    <PenTool size={18} className="text-amber-600" />
                                                </div>
                                                <p className={`text-sm font-medium ${themeCSS.cardTitle}`}>Waiting for {companyName} to sign</p>
                                                {step.step_result?.agreement_name && (
                                                    <p className={`text-xs mt-1 ${themeCSS.cardMuted}`}>{step.step_result.agreement_name}</p>
                                                )}
                                                {step.step_result?.participants?.find(p => p.role === 'customer')?.status === 'signed' ? (
                                                    <p className={`text-xs mt-1 ${themeCSS.cardMuted}`}>You've signed. Waiting for {companyName} to counter-sign.</p>
                                                ) : (
                                                    <p className={`text-xs mt-1 ${themeCSS.cardMuted}`}>{companyName} needs to sign this document before you.</p>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <div className={`mx-auto flex h-10 w-10 items-center justify-center rounded-full mb-2 bg-amber-50`}>
                                                    <PenTool size={18} className="text-amber-600" />
                                                </div>
                                                <p className={`text-sm font-medium ${themeCSS.cardTitle}`}>Document ready to sign</p>
                                                {step.step_result?.agreement_name && (
                                                    <p className={`text-xs mt-1 ${themeCSS.cardMuted}`}>{step.step_result.agreement_name}</p>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {/* Buttons: customer can sign */}
                                    {hasAgreement && step.step_result?.current_signer !== 'admin' && (
                                        <>
                                            <button
                                                onClick={() => handleOpenSigning(step)}
                                                disabled={isLoading}
                                                className="w-full py-3 rounded-2xl text-sm font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-60"
                                                style={{ backgroundColor: primaryColor }}
                                            >
                                                <PenTool size={16} />
                                                {isLoading ? 'Loading...' : 'Sign Document'}
                                            </button>
                                            <button
                                                onClick={() => handleIveSigned(step)}
                                                disabled={isLoading}
                                                className={`w-full py-2.5 rounded-2xl text-sm font-medium border ${themeCSS.cardText} ${themeCSS.cardBorder} ${themeCSS.cardHover} disabled:opacity-60`}
                                            >
                                                {isLoading ? 'Checking...' : "I've Signed This"}
                                            </button>
                                        </>
                                    )}

                                    {/* Buttons: waiting for admin */}
                                    {hasAgreement && step.step_result?.current_signer === 'admin' && (
                                        <button
                                            onClick={() => handleCheckSigningStatus(step)}
                                            disabled={isLoading}
                                            className={`w-full py-2.5 rounded-2xl text-sm font-medium border ${themeCSS.cardText} ${themeCSS.cardBorder} ${themeCSS.cardHover} disabled:opacity-60`}
                                        >
                                            {isLoading ? 'Checking...' : 'Check Status'}
                                        </button>
                                    )}
                                </div>
                            );
                        })()}

                        {/* Submit / Acknowledge button */}
                        {(stepType === 'standard' || stepType === 'info') && (
                            <button
                                onClick={() => stepType === 'info' ? handleAcknowledgeStep(step) : handleSubmitStep(step)}
                                disabled={submitting}
                                className="w-full mt-2 py-3 rounded-2xl text-sm font-semibold text-white transition-opacity disabled:opacity-60"
                                style={{ backgroundColor: primaryColor }}
                            >
                                {submitting ? 'Submitting...' : stepType === 'info' ? 'Got it' : 'Submit'}
                            </button>
                        )}
                    </div>
                </div>
            );
        }

        function MessagesTab() {
            const isDisabled = ['COMPLETED', 'CANCELLED'].includes(instance?.status);
            return (
                <>
                    {/* Messages list — pb accounts for the fixed input bar height */}
                    <div className="space-y-3 pb-32 lg:pb-24">
                        {messagesLoading ? (
                            <div className="space-y-3">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className={`h-16 rounded-2xl ${themeCSS.skeleton} animate-pulse`} />
                                ))}
                            </div>
                        ) : messages.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-16 text-center">
                                <MessageSquare size={36} className={`mb-2 ${themeCSS.cardMuted}`} strokeWidth={1.5} />
                                <p className={`text-sm ${themeCSS.cardMuted}`}>No messages yet</p>
                            </div>
                        ) : (
                            <>
                                {messages.map(msg => {
                                    const isTenant = msg.sender_role === 'tenant';
                                    return (
                                        <div key={msg.id} className={`flex ${isTenant ? 'justify-end' : 'justify-start'}`}>
                                            <div
                                                className="max-w-[80%] rounded-2xl px-4 py-3"
                                                style={isTenant
                                                    ? { backgroundColor: theme === 'dark' ? '#334155' : '#EDE9F7' }
                                                    : { backgroundColor: theme === 'dark' ? '#1e293b' : '#F3F4F6' }}
                                            >
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-xs font-semibold" style={isTenant ? { color: primaryColor } : undefined}>
                                                        <span className={isTenant ? '' : themeCSS.cardSubtext}>{isTenant ? 'You' : msg.sender_name || 'Admin'}</span>
                                                    </span>
                                                    <span className={`text-[11px] ${themeCSS.cardMuted}`}>{moment(msg.created_at).format('D MMM YYYY, HH:mm')}</span>
                                                </div>
                                                {msg.subject && <p className={`text-xs font-semibold mb-0.5 ${themeCSS.cardTitle}`}>{msg.subject}</p>}
                                                <p className={`text-sm ${themeCSS.cardText}`}>{msg.body}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                                <p className={`text-xs text-center px-4 py-2 ${themeCSS.cardMuted}`}>
                                    Messages are reviewed in the order that they are received. We will come back to you as soon as we can.
                                </p>
                            </>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input bar — fixed above bottom tab bar on mobile, fixed bottom on desktop */}
                    <div
                        className={`fixed bottom-0 left-0 right-0 z-30 px-3 pt-2.5 pb-28 lg:pb-4 ${themeCSS.cardBg}`}
                        style={{ borderTop: `1px solid ${theme === 'dark' ? '#334155' : '#E5E5EA'}` }}
                    >
                        <div className="max-w-2xl mx-auto">
                            {isDisabled ? (
                                <div className={`flex items-center justify-center rounded-full px-4 py-3 ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-100'}`}>
                                    <p className={`text-sm ${themeCSS.cardMuted}`}>Messaging is closed for this request</p>
                                </div>
                            ) : (
                                <div className="flex items-end gap-2">
                                    <div className={`flex-1 flex items-center rounded-full px-4 py-2.5 ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-100'}`}>
                                        <input
                                            type="text"
                                            value={messageBody}
                                            onChange={e => setMessageBody(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                                            placeholder="Type a message..."
                                            className={`flex-1 text-sm bg-transparent ${themeCSS.cardText} min-w-0 border-0 outline-none ring-0 focus:outline-none focus:ring-0`}
                                            style={{ caretColor: primaryColor }}
                                        />
                                    </div>
                                    <button
                                        onClick={handleSendMessage}
                                        disabled={!messageBody.trim() || sendingMessage}
                                        className="w-10 h-10 rounded-full flex items-center justify-center transition-opacity disabled:opacity-40 flex-shrink-0"
                                        style={{ backgroundColor: primaryColor }}
                                    >
                                        <Send size={17} className="text-white" strokeWidth={2} />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            );
        }
    }

    // ── Plus button ────────────────────────────────────────────────────
    const plusButton = view === 'list' ? (
        <button
            onClick={openNewRequest}
            className="w-9 h-9 rounded-full flex items-center justify-center text-white shadow-sm"
            style={{ backgroundColor: 'rgba(255,255,255,0.25)' }}
            aria-label="New request"
        >
            <Plus size={20} />
        </button>
    ) : null;

    // ── Step count subtitle for detail header ──────────────────────────
    const detailSubtitle = (() => {
        if (view !== 'detail' || !instance?.steps) return undefined;
        const visible = instance.steps.filter(s => s.is_conditionally_visible !== false && s.is_conditionally_visible !== 0);
        const done = visible.filter(s => s.status === 'COMPLETED').length;
        return `${done} of ${visible.length} steps complete`;
    })();

    return (
        <div className="min-h-screen" style={{ backgroundColor: themeCSS.pageBg }}>
            <HeaderBackground
                pageTitle={view === 'detail' && instance ? instance.task_flow_name : 'Requests'}
                pageSubtitle={detailSubtitle}
                onBack={view === 'detail' ? () => { setView('list'); setInstance(null); fetchInstances(false); } : undefined}
                rightAction={plusButton}
            />

            <div className="max-w-2xl mx-auto px-4 py-4 pb-28">
                {view === 'list' ? ListView() : DetailView()}
            </div>

            {/* New Request — bottom sheet on mobile, centered modal on desktop */}
            {showNewRequest && (
                <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center">
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-fadeIn" onClick={() => setShowNewRequest(false)} />
                    <div className={`relative w-full max-w-lg rounded-t-3xl lg:rounded-2xl shadow-2xl animate-slideUp ${themeCSS.cardBg} p-5 pb-8 lg:pb-5`} style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                        {/* Handle — mobile only */}
                        <div className={`lg:hidden w-10 h-1 rounded-full mx-auto mb-4 ${theme === 'dark' ? 'bg-slate-600' : 'bg-gray-200'}`} />
                        <div className="flex items-center justify-between mb-4">
                            <h2 className={`text-base font-semibold ${themeCSS.cardTitle}`}>New Request</h2>
                            <button onClick={() => setShowNewRequest(false)} className={themeCSS.cardMuted}>
                                <X size={20} />
                            </button>
                        </div>

                        {flowsLoading ? (
                            <div className="space-y-2">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className={`h-14 rounded-xl ${themeCSS.skeleton} animate-pulse`} />
                                ))}
                            </div>
                        ) : flows.length === 0 ? (
                            <p className={`text-sm text-center py-4 ${themeCSS.cardMuted}`}>No request types available</p>
                        ) : (
                            <div className="space-y-2 mb-4">
                                {flows.map(flow => {
                                    const selected = selectedFlowId === flow.id;
                                    return (
                                        <button
                                            key={flow.id}
                                            onClick={() => setSelectedFlowId(flow.id)}
                                            className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left border transition-colors ${selected ? 'border-transparent text-white' : `${themeCSS.cardBorder} ${themeCSS.cardHover}`}`}
                                            style={selected ? { backgroundColor: primaryColor } : undefined}
                                        >
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${selected ? 'bg-white/20' : theme === 'dark' ? 'bg-slate-700' : 'bg-gray-100'}`}>
                                                <ClipboardCheck size={16} style={selected ? { color: 'white' } : { color: primaryColor }} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-sm font-medium ${selected ? 'text-white' : themeCSS.cardTitle}`}>{flow.name}</p>
                                                {flow.description && <p className={`text-xs mt-0.5 ${selected ? 'text-white/70' : themeCSS.cardMuted}`}>{flow.description}</p>}
                                            </div>
                                            {selected && <Check size={16} className="text-white flex-shrink-0" />}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        <button
                            onClick={handleContinue}
                            disabled={!selectedFlowId || creating}
                            className="w-full py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-40 transition-opacity"
                            style={{ backgroundColor: primaryColor }}
                        >
                            {creating ? 'Creating...' : 'Continue'}
                        </button>
                    </div>
                </div>
            )}
            {/* AllPay Payment Modal */}
            {paymentModal && (
                <>
                    {/* Backdrop */}
                    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={paymentResult ? closePaymentModal : undefined} />

                    {/* Modal — bottom sheet on mobile, centered on desktop */}
                    <div className="fixed inset-0 z-50 flex items-end lg:items-center lg:justify-center lg:p-4">
                        <div className={`w-full animate-slideUp flex flex-col rounded-t-2xl lg:rounded-2xl shadow-2xl lg:max-w-lg overflow-hidden ${
                            theme === 'dark' ? 'bg-slate-800' : 'bg-white'
                        }`} style={{ height: '92vh', maxHeight: '92vh' }}>
                            {/* Drag handle — mobile only */}
                            <div className="flex justify-center pt-3 pb-1 flex-shrink-0 lg:hidden">
                                <div className={`w-10 h-1 rounded-full ${theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'}`} />
                            </div>
                            {/* Header */}
                            <div className="flex items-center justify-between px-5 pb-3 lg:pt-5 flex-shrink-0">
                                <h2 className={`text-lg font-bold ${themeCSS.cardTitle}`}>Make Payment</h2>
                                {/* Mobile: text Done button */}
                                <button onClick={closePaymentModal} className="text-sm font-semibold lg:hidden" style={{ color: primaryColor }}>Done</button>
                                {/* Desktop: X button */}
                                <button
                                    onClick={closePaymentModal}
                                    className={`hidden lg:flex w-8 h-8 rounded-full items-center justify-center transition-colors ${theme === 'dark' ? 'hover:bg-slate-700' : 'hover:bg-gray-200'}`}
                                >
                                    <X size={18} className={themeCSS.cardMuted} strokeWidth={2} />
                                </button>
                            </div>
                            {/* Content */}
                            <div className="flex-1 min-h-0">
                                {paymentResult ? (
                                    <div className="flex flex-col items-center justify-center h-full px-5">
                                        <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${paymentResult === 'success' ? 'bg-green-100' : 'bg-red-100'}`}>
                                            {paymentResult === 'success'
                                                ? <CreditCard size={28} className="text-green-600" />
                                                : <X size={28} className="text-red-500" />
                                            }
                                        </div>
                                        <p className={`text-lg font-bold mb-2 ${paymentResult === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                                            {paymentResult === 'success' ? 'Payment Successful' : paymentResult === 'cancelled' ? 'Payment Cancelled' : 'Payment Failed'}
                                        </p>
                                        {paymentResult === 'success' && (
                                            <p className={`text-sm text-center ${themeCSS.cardMuted}`}>
                                                It can take up to one week for the payment to appear on your account.
                                            </p>
                                        )}
                                        <button
                                            onClick={closePaymentModal}
                                            className="mt-6 px-8 py-3 rounded-xl text-sm font-semibold text-white"
                                            style={{ backgroundColor: primaryColor }}
                                        >
                                            {paymentResult === 'success' ? 'Done' : 'Close'}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex flex-col h-full">
                                        <iframe
                                            name="RequestsPaymentIFrame"
                                            id="RequestsPaymentIFrame"
                                            title="Make Payment"
                                            className="w-full flex-1 border-0"
                                        />
                                        <p className={`text-xs text-center py-2.5 flex-shrink-0 border-t ${theme === 'dark' ? 'border-slate-700' : 'border-gray-100'} ${themeCSS.cardMuted}`}>Please do not refresh the page while your payment is being processed</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
